import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { reportSwallowed } from "../lib/reportSwallowed";
import { qk, readOrgScoped } from "./queries";
import { useNewFieldPrompt } from "../stores/newFieldPrompt";
import { useBoundaryOverlap } from "../stores/boundaryOverlap";
import type { DashboardField } from "../types/contract";

// Thrown when the user declines an overlapping boundary on a PRE-create path — a deliberate user
// choice, not a failure, so callers must swallow it silently rather than surfacing an error.
export const CANCELLED_OVERLAP = "CANCELLED_OVERLAP";

// Collapse to one geometry: a single polygon passes through, several become a MultiPolygon.
export function toBareGeometry(input: unknown): unknown | null {
  const polys: unknown[][] = [];
  const walk = (g: unknown): void => {
    const o = g as {
      type?: string;
      coordinates?: unknown;
      geometry?: unknown;
      features?: unknown[];
      geometries?: unknown[];
    } | null;
    if (!o || typeof o !== "object") return;
    if (o.type === "FeatureCollection")
      return void (o.features ?? []).forEach(walk);
    if (o.type === "Feature") return walk(o.geometry);
    if (o.type === "GeometryCollection")
      return void (o.geometries ?? []).forEach(walk);
    if (o.type === "Polygon" && Array.isArray(o.coordinates))
      polys.push(o.coordinates as unknown[]);
    if (o.type === "MultiPolygon" && Array.isArray(o.coordinates))
      for (const p of o.coordinates as unknown[][]) polys.push(p);
  };
  walk(input);
  if (!polys.length) return null;
  return polys.length === 1
    ? { type: "Polygon", coordinates: polys[0] }
    : { type: "MultiPolygon", coordinates: polys };
}
export const isOverlapCancel = (e: unknown): boolean =>
  e instanceof Error && e.message === CANCELLED_OVERLAP;

// Centralizes the three create-field flows + refreshing the table. Stable callbacks
// (so the global drag/drop listener doesn't re-bind every render).
export function useNewField() {
  const qc = useQueryClient();
  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: qk.fields }),
    [qc],
  );
  const offerJd = useCallback(
    (field: DashboardField) => useNewFieldPrompt.getState().ask(field),
    [],
  );

  // A new boundary auto-kicks the covariate collection: dispatch CV2 the moment the boundary
  // exists. Async dedupes on {field_id}:covariate_build, so this is safe even where async already
  // auto-chains (JD/upload) — and it covers drawn boundaries, which are a BE-light direct write
  // that doesn't go through async's ingest DAG.
  const collectCovariates = useCallback((fieldId: string) => {
    void api
      .buildCovariates(fieldId)
      .catch((err) => reportSwallowed("build covariates", err));
  }, []);

  // Never blocks the create on an infrastructure problem: if the check itself fails we proceed (a
  // missed warning is recoverable; refusing to add a field because a check 500'd is not).
  const overlapGate = useCallback(
    async (
      body: { geojson?: unknown; field_id?: string },
      name: string,
      alreadyCreated: boolean,
    ): Promise<boolean> => {
      let hits;
      try {
        hits = await api.checkBoundaryOverlap(body);
      } catch {
        return true; // check unavailable → don't stand between the user and their field
      }
      if (!hits?.length) return true; // the common case: no overlap, no interruption
      return useBoundaryOverlap
        .getState()
        .ask({ name, candidate: body.geojson ?? null, hits, alreadyCreated });
    },
    [],
  );

  // Ask AFTER a server-parsed boundary lands, and undo the create if the user says it's a duplicate.
  const gateAfterParse = useCallback(
    async (fieldId: string, name: string) => {
      const keep = await overlapGate({ field_id: fieldId }, name, true);
      if (keep) {
        collectCovariates(fieldId); // only NOW is the field confirmed — see the ordering note below
        return;
      }
      // the JD offer was opened at create time; it must not linger pointing at a field we're removing
      useNewFieldPrompt.getState().dismiss(fieldId);
      try {
        await api.deleteField(fieldId);
      } catch {
        useBoundaryOverlap.getState().flagRemovalFailed(name);
      }
      await refresh();
    },
    [overlapGate, refresh, collectCovariates],
  );

  // Uploaded shp/kml/kmz boundaries are parsed ASYNC (GCS finalize → Eventarc → compute)
  // with no job_id the FE can track — so the just-created field shows acres 0 / no boundary
  // until the parse lands seconds later. Poll the dashboard (+ the field's layers, to refresh
  // an open map) on a backoff until has_boundary flips, then kick the covariate build + stop.
  const pollBoundary = useCallback(
    (fieldId: string) => {
      const delays = [1500, 2500, 4000, 6000, 8000, 12000]; // ~34s total, then give up
      let i = 0;
      const tick = async () => {
        await qc.refetchQueries({ queryKey: qk.fields });
        void qc.invalidateQueries({ queryKey: qk.layers(fieldId) });
        const f = (readOrgScoped<DashboardField[]>(qc, qk.fields) ?? []).find(
          (x) => x.field_id === fieldId,
        );
        if (f?.has_boundary) {
          // ORDER MATTERS: the gate runs FIRST and `collectCovariates` moves into its keep-branch.
          // Collecting first would dispatch a ~30-min GEE build against a field the user is about
          // to decline, leaving orphaned compute writing rows for a deleted field id with no way to
          // cancel it.
          void gateAfterParse(fieldId, f.field_name);
          return;
        }
        if (i < delays.length)
          window.setTimeout(() => void tick(), delays[i++]);
      };
      window.setTimeout(() => void tick(), delays[i++]);
    },
    [qc, gateAfterParse],
  );

  const fromBoundary = useCallback(
    async (name: string, geojson: unknown): Promise<DashboardField> => {
      // we hold the geometry here → ask before writing anything
      if (!(await overlapGate({ geojson }, name, false)))
        throw new Error(CANCELLED_OVERLAP);
      const f = await api.createFieldFromBoundary(name, geojson);
      await refresh();
      collectCovariates(f.field_id); // drawn boundary is written synchronously → collect now
      offerJd(f);
      return f;
    },
    [refresh, collectCovariates, offerJd, overlapGate],
  );

  const fromFile = useCallback(
    async (file: File): Promise<DashboardField> => {
      const name = file.name.replace(/\.[^.]+$/, "") || "New field";
      if (/\.(geo)?json$/i.test(file.name)) {
        const geojson = JSON.parse(await file.text());
        // a FeatureCollection can't be drawn or ST_GeomFromGeoJSON'd — gate on the bare geometry when we can get one
        const bare = toBareGeometry(geojson);
        if (bare && !(await overlapGate({ geojson: bare }, name, false)))
          throw new Error(CANCELLED_OVERLAP);
        const f = await api.createFieldFromBoundary(name, geojson);
        await refresh();
        collectCovariates(f.field_id); // inline geojson boundary is written synchronously
        offerJd(f);
        return f;
      }
      const f = await api.createFieldFromFile(name, file); // shp-zip / kml / kmz -> async parses boundary
      await refresh();
      pollBoundary(f.field_id); // refresh + collect covariates once the async parse stores the boundary
      offerJd(f);
      return f;
    },
    [refresh, pollBoundary, collectCovariates, offerJd, overlapGate],
  );

  const fromJd = useCallback(
    async (
      jdOrgId: string,
      jdFieldId: string,
      jdName?: string,
    ): Promise<DashboardField> => {
      let f = await api.createFieldFromJd(jdOrgId, jdFieldId, jdName);
      // #3: the new field must carry the JD field's NAME, not JD's opaque id.
      if (jdName && f.field_name !== jdName) {
        try {
          f = await api.renameField(f.field_id, jdName);
        } catch {
          /* keep the created name on failure */
        }
      }
      await refresh();
      pollBoundary(f.field_id); // JD boundary lands async → collect covariates once it's stored
      offerJd(f);
      return f;
    },
    [refresh, pollBoundary, offerJd],
  );

  return { fromBoundary, fromFile, fromJd };
}
